import classNames from "classnames";
import React, { MouseEvent } from "react";
import { useElementId } from "../../hooks/use-element-id";
import { ButtonProps } from "../button";
import { CollapseProps } from "../collapse/collapse.types";
import { Separator } from "../separator/separator";
import { CSS_CLASSES } from "./accordion-css-classes";
import { AccordionSectionInternalProps, AccordionSectionProps } from "./accordion-section";

export interface AccordionProps
  extends Pick<CollapseProps, "preserveDomElement" | "lazyInitialCollapse">,
    Pick<ButtonProps, "ripple"> {
  /**
   * The HTML id attribute to be applied to the root element of this component.
   * If not passed, a unique `id` will be generated and applied.
   */
  id?: string;

  /**
   * Controls the additional classNames passed to the component styling
   * This can be a string containing multiple classes space separated
   */
  className?: string;

  /**
   * Controls the additional classNames passed to the separator component used within the Accordion.
   * This can be a string containing multiple classes space separated.
   */
  separatorClassName?: string;

  /**
   * Controls if the separator above the first AccordionSection is rendered or not.
   */
  hideFirstSeparator?: boolean;

  /**
   * Controls if the separator below the last AccordionSection is rendered or not.
   */
  hideLastSeparator?: boolean;

  /**
   * Index of sections that should be expanded on the first component render.
   * This is a zero-based index, so first section is 0, second section is 1, ...
   * @default []
   */
  defaultActiveIndex?: number[];

  /**
   * Set the currently expanded accordion sections. This is a zero-based index, so first section is 0, second section is 1, ...
   * This enables controlled mode, which also requires onActivate to be set
   * @default null
   */
  activeIndex?: number[];

  /**
   * Function called on expand/collapse accordion sections.
   * It will be called with the event which is click event, array of index that will be active and the array of previous active index.
   */
  onActivate?: (
    event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    activeIndex: number[],
    previousActiveIndex: number[]
  ) => void;

  /**
   * Accordion component works in two modes:
   * single - only one section should be expanded at a time
   * multiple - it allows to expand multiple sections at a time
   * @default single
   */
  mode?: "single" | "multiple";

  /**
   * Color option for the header.
   *
   * @default interactive
   */
  color?: "interactive" | "primary" | "white";

  /**
   * A set of `AccordionSection` components.
   */
  children: React.ReactNode;
}

/**
 * The `Accordion` allows users to toggle the display of sections of content.
 *
 * `Accordion` works in two modes - single and multiple.
 *
 * Component is uncontrolled by default but can be controlled by `activeIndex` and `onActivate` props.
 *
 *  Read on to understand the UX intent behind each of the prop values.
 */
export const Accordion = ({
  id,
  className: classNameProp,
  mode = "single",
  color = "interactive",
  defaultActiveIndex = [],
  onActivate = () => {},
  activeIndex,
  children,
  separatorClassName,
  hideFirstSeparator,
  hideLastSeparator,
  ripple,
  preserveDomElement,
  lazyInitialCollapse,
  ...restProps
}: AccordionProps) => {
  const accordionId = useElementId(id, "accordion");
  const className = classNames(classNameProp, CSS_CLASSES.ACCORDION_ROOT);
  const [activeIndexState, setActiveIndex] = React.useState(
    mode === "single" && defaultActiveIndex[0] !== undefined ? [defaultActiveIndex[0]] : defaultActiveIndex
  );

  const handleToggleActive = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, index: number, active: boolean) => {
      if (mode === "single") {
        const newValue = active ? [] : [index];
        if (!activeIndex) {
          setActiveIndex(newValue);
        }
        onActivate(event, newValue, activeIndex);
      } else {
        const currentActiveState = activeIndex || activeIndexState;
        const newValue = active
          ? currentActiveState.filter((stateIndex) => stateIndex !== index)
          : [...currentActiveState, index];
        onActivate(event, newValue, activeIndex);
        if (!activeIndex) {
          setActiveIndex(newValue);
        }
      }
    },
    [activeIndexState, mode, onActivate, activeIndex]
  );

  return (
    <div id={accordionId} className={className} {...restProps}>
      {!hideFirstSeparator && <Separator gap="none" orientation="horizontal" className={separatorClassName} />}
      {React.Children.toArray(children).map((child, index, { length }) => {
        if (React.isValidElement<AccordionSectionProps & AccordionSectionInternalProps>(child)) {
          return React.cloneElement(child, {
            preserveDomElement,
            lazyInitialCollapse,
            ripple,
            id: `${accordionId}-accordion-section-${index}`,
            ...child.props,
            index,
            onAccordionSectionClick: handleToggleActive,
            active: (activeIndex || activeIndexState).some((elem) => elem === index),
            separatorClassName: separatorClassName,
            hideSeparator: index === length - 1 && hideLastSeparator,
            color,
          });
        }
        return null;
      })}
    </div>
  );
};
